/**
 * MADEKTA — facets.js
 * Filtros dinámicos (Search & Discovery), ordenamiento, filtros activos y
 * carga progresiva. Sin JavaScript todo funciona con formularios y enlaces.
 */
import { debounce, request, parseHTML, toast } from '@madekta/core';

/**
 * Serializa el formulario de filtros omitiendo valores vacíos.
 * @param {HTMLFormElement} form
 */
export function serializeFacets(form) {
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
  }
  return params;
}

const desktop = window.matchMedia('(min-width: 1024px)');

class FacetResults extends HTMLElement {
  connectedCallback() {
    this.controller = null;
    this.cache = new Map();
    this.refreshDebounced = debounce(() => this.applyForm(), 600);
    this.syncDrawerMode();
    desktop.addEventListener('change', () => this.syncDrawerMode());

    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-facet-input]')) return;
      if (event.target.hasAttribute('data-debounce')) this.refreshDebounced();
      else this.applyForm();
    });
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-debounce]')) this.refreshDebounced();
    });
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-facets-form]')) return;
      event.preventDefault();
      this.applyForm();
    });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.load(new URL(link.href, window.location.origin).searchParams);
        return;
      }
      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    });
    window.addEventListener('popstate', () => this.load(new URLSearchParams(window.location.search), false));
  }

  get form() {
    return this.querySelector('[data-facets-form]');
  }

  /** En escritorio el panel es una barra lateral siempre visible. */
  syncDrawerMode() {
    const drawer = this.querySelector('#FacetsDrawer');
    if (!drawer) return;
    if (desktop.matches) {
      drawer.close?.();
      drawer.removeAttribute('inert');
    } else if (!drawer.hasAttribute('open')) {
      drawer.setAttribute('inert', '');
    }
  }

  applyForm() {
    this.load(serializeFacets(this.form));
  }

  sectionUrl(params) {
    const query = new URLSearchParams(params);
    query.delete('page');
    query.set('section_id', this.dataset.section);
    return `${window.location.pathname}?${query}`;
  }

  /**
   * @param {URLSearchParams} params
   * @param {boolean} [push]
   */
  async load(params, push = true) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url = this.sectionUrl(params);
    this.setAttribute('aria-busy', 'true');
    try {
      const html = this.cache.get(url) ?? (await request(url, { as: 'text', signal: this.controller.signal }));
      this.cache.set(url, html);
      this.render(parseHTML(html));
      if (push) {
        const visible = new URLSearchParams(params);
        visible.delete('page');
        const query = visible.toString();
        window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      }
    } catch (error) {
      if (!this.controller.signal.aborted) toast(error.message, 'error');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {Document} doc */
  render(doc) {
    const fresh = doc.querySelector('facet-results');
    if (!fresh) return;
    const openStates = [...this.querySelectorAll('.facet')].map((details) => details.open);

    const swap = (selector) => {
      const next = fresh.querySelector(selector);
      const current = this.querySelector(selector);
      if (next && current) current.replaceWith(next);
      else if (current && !next) current.remove();
      else if (next && !current && selector === '[data-active-filters]') {
        this.querySelector('.facets__toolbar')?.after(next);
      }
    };
    swap('.facets__toolbar');
    swap('[data-active-filters]');
    swap('[data-results]');

    const body = this.querySelector('#FacetsDrawer .drawer__body');
    const nextBody = fresh.querySelector('#FacetsDrawer .drawer__body');
    if (body && nextBody) {
      body.replaceChildren(...nextBody.childNodes);
      this.querySelectorAll('.facet').forEach((details, i) => {
        if (openStates[i] !== undefined) details.open = openStates[i];
      });
    }
    this.dispatchEvent(new CustomEvent('madekta:content-replaced', { bubbles: true }));
  }

  /** @param {HTMLAnchorElement} button */
  async loadMore(button) {
    button.setAttribute('aria-busy', 'true');
    try {
      const next = new URL(button.href, window.location.origin);
      next.searchParams.set('section_id', this.dataset.section);
      const doc = parseHTML(await request(next.toString(), { as: 'text' }));
      const items = doc.querySelectorAll('[data-results-grid] > li');
      const grid = this.querySelector('[data-results-grid]');
      const firstNew = items[0];
      grid.append(...items);
      this.querySelector('.catalog__more')?.replaceWith(doc.querySelector('.catalog__more') ?? '');
      const pagination = doc.querySelector('[data-pagination]');
      if (pagination) this.querySelector('[data-pagination]')?.replaceWith(pagination);
      firstNew?.querySelector('a')?.focus({ preventScroll: true });
      grid.dispatchEvent(new CustomEvent('madekta:content-replaced', { bubbles: true }));
    } catch (error) {
      toast(error.message, 'error');
      button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('facet-results', FacetResults);
